import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

import staffIdService from '../../../services/staffIdService';
import { useAuth } from '../../../contexts/AuthContext';

const PendingApprovalsPanel = () => {
  const { user } = useAuth();
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState('approve');
  const [comments, setComments] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [userRole, setUserRole] = useState('HR Coordinator');

  useEffect(() => {
    loadPendingApprovals();
  }, [userRole]);

  const loadPendingApprovals = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await staffIdService?.getPendingStaffIdApprovals(userRole);
      setPendingApprovals(data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load pending approvals');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveClick = (approval) => {
    setSelectedApproval(approval);
    setActionType('approve');
    setComments('');
    setRejectionReason('');
    setShowActionModal(true);
  };

  const handleRejectClick = (approval) => {
    setSelectedApproval(approval);
    setActionType('reject');
    setComments('');
    setRejectionReason('');
    setShowActionModal(true);
  };

  const handleSubmitAction = async () => {
    if (!selectedApproval) return;

    if (actionType === 'reject' && !rejectionReason?.trim()) {
      setError('Rejection reason is required');
      return;
    }

    try {
      setLoading(true);
      setError('');

      if (actionType === 'approve') {
        await staffIdService?.approveStaffIdStep(
          selectedApproval?.id,
          user?.email || 'current_user',
          comments
        );
        setSuccess('Approval step completed successfully');
      } else {
        await staffIdService?.rejectStaffIdStep(
          selectedApproval?.id,
          user?.email || 'current_user',
          rejectionReason,
          comments
        );
        setSuccess('Request rejected successfully');
      }

      setShowActionModal(false);
      setSelectedApproval(null);
      await loadPendingApprovals();
    } catch (err) {
      setError(err?.message || 'Failed to process approval action');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-foreground">Pending Approvals</h2>
          <p className="text-sm text-muted-foreground mt-1">Staff ID cards awaiting your approval</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-muted-foreground">Your Role:</label>
          <select
            value={userRole}
            onChange={(e) => setUserRole(e?.target?.value)}
            className="px-3 py-2 border border-border rounded-md text-sm bg-background text-foreground"
          >
            <option value="HR Coordinator">HR Coordinator</option>
            <option value="Department Manager">Department Manager</option>
            <option value="Security Officer">Security Officer</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="bg-error/10 border border-error text-error px-4 py-3 rounded-md mb-4 flex items-start gap-2">
          <Icon name="AlertCircle" size={18} className="mt-0.5" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {success && (
        <div className="bg-success/10 border border-success text-success px-4 py-3 rounded-md mb-4 flex items-start gap-2">
          <Icon name="CheckCircle" size={18} className="mt-0.5" />
          <span className="text-sm">{success}</span>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Icon name="Loader2" className="animate-spin h-8 w-8 text-primary" />
        </div>
      ) : pendingApprovals?.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Icon name="CheckCircle" size={48} className="mx-auto mb-4 opacity-50" />
          <p>No pending approvals</p>
        </div>
      ) : (
        <div className="space-y-4">
          {pendingApprovals?.map((approval) => (
            <div key={approval?.id} className="border border-border rounded-lg p-4 hover:bg-muted/50 transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-sm font-semibold text-foreground">
                      {approval?.entityMetadata?.employee_name}
                    </h3>
                    <span className="text-xs px-2 py-1 rounded-full bg-warning/20 text-warning">
                      Step {approval?.stepOrder}: {approval?.stepName}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                    <p>Employee #: {approval?.entityMetadata?.employee_number}</p>
                    <p>Department: {approval?.entityMetadata?.department}</p>
                    <p>Position: {approval?.entityMetadata?.position}</p>
                    <p>Submitted: {new Date(approval?.initiatedAt)?.toLocaleDateString()}</p>
                  </div>
                  {approval?.timeoutAt && (
                    <p className="text-xs text-warning mt-2">
                      <Icon name="Clock" size={12} className="inline mr-1" />
                      Timeout: {new Date(approval?.timeoutAt)?.toLocaleString()}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => handleApproveClick(approval)}
                    size="sm"
                    className="bg-success hover:bg-success/90"
                  >
                    <Icon name="Check" size={16} className="mr-1" />
                    Approve
                  </Button>
                  <Button
                    onClick={() => handleRejectClick(approval)}
                    size="sm"
                    variant="outline"
                    className="border-error text-error hover:bg-error/10"
                  >
                    <Icon name="X" size={16} className="mr-1" />
                    Reject
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Action Modal */}
      {showActionModal && selectedApproval && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold text-foreground mb-4">
              {actionType === 'approve' ? 'Approve Request' : 'Reject Request'}
            </h3>

            <div className="mb-4">
              <p className="text-sm text-muted-foreground mb-2">Employee:</p>
              <p className="text-sm font-medium text-foreground">
                {selectedApproval?.entityMetadata?.employee_name}
              </p>
            </div>

            {actionType === 'reject' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-foreground mb-2">
                  Rejection Reason *
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e?.target?.value)}
                  className="w-full px-3 py-2 border border-border rounded-md text-sm bg-background text-foreground"
                  rows={3}
                  placeholder="Please provide a reason for rejection..."
                />
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium text-foreground mb-2">
                Comments (Optional)
              </label>
              <textarea
                value={comments}
                onChange={(e) => setComments(e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-md text-sm bg-background text-foreground"
                rows={3}
                placeholder="Add any additional comments..."
              />
            </div>

            <div className="flex justify-end gap-3">
              <Button
                onClick={() => {
                  setShowActionModal(false);
                  setSelectedApproval(null);
                }}
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmitAction}
                className={actionType === 'approve' ? 'bg-success hover:bg-success/90' : 'bg-error hover:bg-error/90'}
              >
                {actionType === 'approve' ? 'Confirm Approval' : 'Confirm Rejection'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingApprovalsPanel;
